import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Eye, Loader2, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  getOuraTraitDetailOptions,
  getOuraTraitOnchainOptions,
  getOuraTraitsOptions,
  ouraMutationOptions,
} from "@/services/queryOptions/ouraQueryOptions";
import {
  buildTraitPayload,
  OuraListParams,
  OuraOnchainTrait,
  OuraTrait,
  OuraTraitItem,
  traitNftSchema,
  traitSchema,
} from "@/types/oura";
import { AdminDataTable } from "../components/AdminDataTable";
import { SecurityNote, TextAreaField, TextField } from "../components/FormControls";

type TraitFormValues = z.infer<typeof traitSchema>;
type TraitNftFormValues = z.infer<typeof traitNftSchema>;

const traitDefaults: TraitFormValues = {
  collection: "",
  max_supply: "0",
  mint_fee_lamports: "0",
  update_fee_lamports: "0",
  royalty_recipients: "",
  royalty_shares_bps: "",
  trait_name: "",
  trait_uri: "",
};

const traitNftDefaults: TraitNftFormValues = {
  payer: "",
  collection: "",
  asset: "",
  name: "",
  uri: "",
  confirmed: false,
};

function short(value?: string | null) {
  if (!value) return "-";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function csv(values?: Array<string | number> | null) {
  return values?.map(String).join(", ") ?? "";
}

function asTraitUri(value?: string | null) {
  return value?.match(/^https?:\/\//) ? value : "";
}

function DetailField({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const displayValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={mono ? "break-all font-mono text-xs" : "break-words text-sm"}>{displayValue}</div>
    </div>
  );
}

function traitValuesFromDetail(trait?: OuraTrait, onchain?: OuraOnchainTrait): TraitFormValues {
  return {
    collection: trait?.collection ?? "",
    max_supply: onchain?.max_supply ?? trait?.maxSupply ?? "0",
    mint_fee_lamports: onchain?.mint_fee_lamports ?? trait?.mintFeeLamports ?? "0",
    update_fee_lamports: onchain?.update_fee_lamports ?? trait?.updateFeeLamports ?? "0",
    royalty_recipients: csv(trait?.royaltyRecipients),
    royalty_shares_bps: csv(trait?.royaltySharesBps),
    trait_name: trait?.traitName ?? "",
    trait_uri: trait?.traitUri ?? "",
  };
}

function TraitForm({
  mode,
  initialValues,
  disabled = false,
  disabledReason,
  onSuccess,
}: {
  mode: "create" | "update";
  initialValues?: TraitFormValues;
  disabled?: boolean;
  disabledReason?: string | null;
  onSuccess?: () => void;
}) {
  const { toast } = useToast();
  const form = useForm<TraitFormValues>({
    resolver: zodResolver(traitSchema),
    defaultValues: initialValues ?? traitDefaults,
  });
  const mutation = useMutation(
    ouraMutationOptions<Record<string, unknown>>(
      mode === "create" ? "POST" : "PUT",
      "/api/v1/oura/trait",
    ),
  );

  useEffect(() => {
    form.reset(initialValues ?? traitDefaults);
  }, [form, initialValues]);

  const submit = form.handleSubmit((values) => {
    const payload = buildTraitPayload(values);
    if (mode === "update" && !payload.collection) {
      form.setError("collection", { message: "Collection is required for updates" });
      return;
    }
    if (payload.num_royalty_recipients !== payload.royalty_shares_bps.length) {
      form.setError("royalty_shares_bps", { message: "Royalty shares must match recipient count" });
      return;
    }
    const body = mode === "create" ? { ...payload, collection: undefined } : payload;
    mutation.mutate(body, {
      onSuccess: (result) => {
        toast({ title: "Trait transaction submitted", description: result.signature });
        if (mode === "create") form.reset(traitDefaults);
        onSuccess?.();
      },
      onError: (error) => {
        toast({
          title: "Trait transaction failed",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{mode === "create" ? "Create Trait Collection" : "Edit On-chain Trait Collection"}</CardTitle>
      </CardHeader>
      <CardContent>
        {disabledReason && (
          <div className="mb-4 flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{disabledReason}</span>
          </div>
        )}
        <form className="grid gap-4 lg:grid-cols-2" onSubmit={submit}>
          {mode === "update" && (
            <TextField
              label="Collection"
              placeholder="So11111111111111111111111111111111111111112"
              registration={form.register("collection")}
              error={form.formState.errors.collection}
            />
          )}
          <TextField label="Max supply" placeholder="100" registration={form.register("max_supply")} error={form.formState.errors.max_supply} />
          <TextField
            label="Mint fee lamports"
            placeholder="1000000"
            registration={form.register("mint_fee_lamports")}
            error={form.formState.errors.mint_fee_lamports}
          />
          <TextField
            label="Update fee lamports"
            placeholder="500000"
            registration={form.register("update_fee_lamports")}
            error={form.formState.errors.update_fee_lamports}
          />
          <TextField
            label="Royalty recipients CSV"
            placeholder="So11111111111111111111111111111111111111112,9xQeWvG816bUx9EPwWQqY1yYbP8m9m9m9m9m9m9m9m9"
            registration={form.register("royalty_recipients")}
            error={form.formState.errors.royalty_recipients}
          />
          <TextField label="Royalty shares BPS CSV" placeholder="500,500" registration={form.register("royalty_shares_bps")} error={form.formState.errors.royalty_shares_bps} />
          <TextField label="Trait name" placeholder="Golden Hat" registration={form.register("trait_name")} error={form.formState.errors.trait_name} />
          <TextField
            label="Trait URI"
            placeholder="https://example.com/traits/golden-hat.json"
            registration={form.register("trait_uri")}
            error={form.formState.errors.trait_uri}
          />
          <Button className="lg:col-span-2" disabled={disabled || mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "create" ? "Create trait" : "Update on-chain trait"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function TraitNftActions({
  collection,
  initialName,
  initialUri,
  onSuccess,
}: {
  collection: string;
  initialName?: string | null;
  initialUri?: string | null;
  onSuccess?: () => void;
}) {
  const { toast } = useToast();
  const form = useForm<TraitNftFormValues>({
    resolver: zodResolver(traitNftSchema),
    defaultValues: {
      ...traitNftDefaults,
      collection,
      name: initialName ?? "",
      uri: asTraitUri(initialUri),
    },
  });
  const mint = useMutation(ouraMutationOptions<Record<string, unknown>>("POST", "/api/v1/oura/trait/nft"));
  const update = useMutation(ouraMutationOptions<Record<string, unknown>>("PUT", "/api/v1/oura/trait/nft"));
  const burn = useMutation(ouraMutationOptions<Record<string, unknown>>("DELETE", "/api/v1/oura/trait"));

  useEffect(() => {
    form.reset({
      ...traitNftDefaults,
      collection,
      name: initialName ?? "",
      uri: asTraitUri(initialUri),
    });
  }, [collection, form, initialName, initialUri]);

  const submit = (action: "mint" | "update" | "burn") =>
    form.handleSubmit((values) => {
      if ((action === "update" || action === "burn") && !values.asset) {
        form.setError("asset", { message: "Asset is required" });
        return;
      }
      if (action === "burn" && !values.confirmed) {
        form.setError("confirmed", { message: "Confirm burn before submitting" });
        return;
      }
      const body =
        action === "burn"
          ? { payer: values.payer, collection: values.collection, trait: values.asset }
          : { payer: values.payer, collection: values.collection, asset: values.asset, name: values.name, uri: values.uri };
      const mutation = action === "mint" ? mint : action === "update" ? update : burn;
      mutation.mutate(body, {
        onSuccess: (result) => {
          toast({ title: "Trait NFT transaction submitted", description: result.signature });
          form.reset({
            ...traitNftDefaults,
            collection,
            name: initialName ?? "",
            uri: asTraitUri(initialUri),
          });
          onSuccess?.();
        },
      });
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Trait NFT Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <SecurityNote />
          </div>
          <TextAreaField
            label="Payer secretKey JSON"
            placeholder="[12,34,56,...]"
            registration={form.register("payer")}
            error={form.formState.errors.payer}
          />
          <TextField label="Collection" placeholder="So11111111111111111111111111111111111111112" registration={form.register("collection")} error={form.formState.errors.collection} />
          <TextField label="Asset" placeholder="So11111111111111111111111111111111111111112" registration={form.register("asset")} error={form.formState.errors.asset} />
          <TextField label="Name" placeholder="Golden Hat #1" registration={form.register("name")} error={form.formState.errors.name} />
          <TextField label="URI" placeholder="https://example.com/traits/golden-hat-1.json" registration={form.register("uri")} error={form.formState.errors.uri} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...form.register("confirmed")} />
            Confirm burn
          </label>
          <div className="flex flex-wrap gap-2 lg:col-span-2">
            <Button type="button" onClick={submit("mint")} disabled={mint.isPending}>Mint</Button>
            <Button type="button" onClick={submit("update")} disabled={update.isPending}>Update</Button>
            <Button type="button" variant="destructive" onClick={submit("burn")} disabled={burn.isPending}>Burn</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function TraitSummary({ trait }: { trait: OuraTrait }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Trait Collection</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <DetailField label="Trait Name" value={trait.traitName} />
        <DetailField label="Collection" value={trait.collection} mono />
        <DetailField label="Traits" value={trait.traitCount ?? trait.traitNames?.length ?? "-"} />
        <DetailField label="Trait Names" value={csv(trait.traitNames)} />
        <DetailField label="Partners" value={csv(trait.partnerNames)} />
        <DetailField label="Categories" value={csv(trait.categoryNames)} />
        <DetailField label="Rarities" value={csv(trait.rarityNames)} />
        <DetailField label="Updated At" value={trait.updatedAt ? new Date(trait.updatedAt).toLocaleString() : "-"} />
      </CardContent>
    </Card>
  );
}

function TraitOnchainSummary({
  trait,
  onchain,
  error,
  onRefresh,
  isFetching,
}: {
  trait: OuraTrait;
  onchain?: OuraOnchainTrait;
  error?: { message: string } | null;
  onRefresh: () => void;
  isFetching: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>On-chain Config</CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
          <RefreshCcw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error.message}</span>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailField label="Collection" value={trait.collection} mono />
          <DetailField label="Authority" value={onchain?.authority ?? trait.authority} mono />
          <DetailField label="Max Supply" value={onchain?.max_supply ?? trait.maxSupply} />
          <DetailField label="User Minted" value={onchain?.user_minted ?? trait.userMinted} />
          <DetailField label="Mint Fee" value={onchain?.mint_fee_lamports ?? trait.mintFeeLamports} />
          <DetailField label="Update Fee" value={onchain?.update_fee_lamports ?? trait.updateFeeLamports} />
        </div>
      </CardContent>
    </Card>
  );
}

function TraitOnchainDrawer({
  trait,
  open,
  onOpenChange,
}: {
  trait: OuraTrait | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useQuery(getOuraTraitOnchainOptions(trait?.collection));
  const initialValues = useMemo(() => traitValuesFromDetail(trait ?? undefined, query.data), [trait, query.data]);
  const disabledReason = query.error?.message ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle>{trait?.traitName ?? short(trait?.collection)}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-73px)]">
          <div className="space-y-6 p-6">
            {query.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading trait on-chain config
              </div>
            )}
            {trait && (
              <>
                <TraitOnchainSummary
                  trait={trait}
                  onchain={query.data}
                  error={query.error}
                  onRefresh={() => query.refetch()}
                  isFetching={query.isFetching}
                />
                <TraitForm
                  mode="update"
                  initialValues={initialValues}
                  disabled={!query.data || Boolean(query.error)}
                  disabledReason={disabledReason}
                  onSuccess={() => query.refetch()}
                />
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function TraitNftDrawer({
  trait,
  open,
  onOpenChange,
}: {
  trait: OuraTrait | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle>Trait NFT Actions</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-73px)]">
          <div className="p-6">
            {trait && <TraitNftActions collection={trait.collection} onSuccess={() => onOpenChange(false)} />}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function TraitItemsTable({
  items,
  selectedItem,
  onSelect,
}: {
  items: OuraTraitItem[];
  selectedItem: OuraTraitItem | null;
  onSelect: (item: OuraTraitItem) => void;
}) {
  if (!items.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Traits Table</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No traits are configured for this collection.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Traits Table</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded border border-zinc-800">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Image</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Rarity</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const active = selectedItem?.id === item.id;
                return (
                  <TableRow key={item.idPublic} className={active ? "bg-primary/10" : undefined}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-12 w-12 rounded border border-zinc-800 object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{item.partnerName}</TableCell>
                    <TableCell>{item.categoryName}</TableCell>
                    <TableCell>{item.rarityName}</TableCell>
                    <TableCell>{item.price}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onSelect(item)}
                        aria-label="View trait actions"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function TraitItemDrawer({
  trait,
  item,
  open,
  onOpenChange,
}: {
  trait: OuraTrait | null;
  item: OuraTraitItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useQuery(getOuraTraitOnchainOptions(trait?.collection));
  const initialValues = useMemo(() => traitValuesFromDetail(trait ?? undefined, query.data), [trait, query.data]);
  const disabledReason = query.error?.message ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle>{item?.name ?? "Trait"}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-73px)]">
          <div className="space-y-6 p-6">
            {item && (
              <Card>
                <CardHeader>
                  <CardTitle>Trait</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <DetailField label="Name" value={item.name} />
                  <DetailField label="Collection" value={item.traitCollection} mono />
                  <DetailField label="Partner" value={item.partnerName} />
                  <DetailField label="Category" value={item.categoryName} />
                  <DetailField label="Rarity" value={item.rarityName} />
                  <DetailField label="Price" value={item.price} />
                  <DetailField label="Image" value={item.image} mono />
                  <DetailField label="Public ID" value={item.idPublic} mono />
                </CardContent>
              </Card>
            )}
            {query.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading trait on-chain config
              </div>
            )}
            {trait && (
              <>
                <TraitOnchainSummary
                  trait={trait}
                  onchain={query.data}
                  error={query.error}
                  onRefresh={() => query.refetch()}
                  isFetching={query.isFetching}
                />
                <TraitForm
                  mode="update"
                  initialValues={initialValues}
                  disabled={!query.data || Boolean(query.error)}
                  disabledReason={disabledReason}
                  onSuccess={() => query.refetch()}
                />
                {item && (
                  <TraitNftActions
                    collection={trait.collection}
                    initialName={item.name}
                    initialUri={item.image}
                    onSuccess={() => onOpenChange(false)}
                  />
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export function TraitDetailPage({ collection }: { collection: string }) {
  const [, setLocation] = useLocation();
  const [isOnchainOpen, setIsOnchainOpen] = useState(false);
  const [isNftActionsOpen, setIsNftActionsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OuraTraitItem | null>(null);
  const query = useQuery(getOuraTraitDetailOptions(collection));
  const trait = query.data?.trait;
  const traitItems = query.data?.traitItems ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => setLocation("/tweeto-admin/oura/traits")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Traits
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-2xl font-semibold">{trait?.traitName ?? "Trait Details"}</h2>
          <p className="text-sm text-zinc-400">Indexed trait collection data. Open actions to inspect or mutate on-chain state.</p>
        </div>
        {trait && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsNftActionsOpen(true)}>Trait NFT Actions</Button>
            <Button onClick={() => setIsOnchainOpen(true)}>On-chain Config</Button>
          </div>
        )}
      </div>

      {query.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading trait detail
        </div>
      )}
      {query.error && (
        <div className="flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{query.error.message}</span>
        </div>
      )}
      {trait && <TraitSummary trait={trait} />}
      {trait && (
        <TraitItemsTable
          items={traitItems}
          selectedItem={selectedItem}
          onSelect={setSelectedItem}
        />
      )}

      <TraitOnchainDrawer trait={trait ?? null} open={isOnchainOpen} onOpenChange={setIsOnchainOpen} />
      <TraitNftDrawer trait={trait ?? null} open={isNftActionsOpen} onOpenChange={setIsNftActionsOpen} />
      <TraitItemDrawer
        trait={trait ?? null}
        item={selectedItem}
        open={selectedItem !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedItem(null);
        }}
      />
    </div>
  );
}

export function TraitManager() {
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 10 });
  const [isCreateTraitOpen, setIsCreateTraitOpen] = useState(false);
  const [, setLocation] = useLocation();
  const query = useQuery(getOuraTraitsOptions(params));
  const openTrait = (collection: string) => setLocation(`/tweeto-admin/oura/traits/${encodeURIComponent(collection)}`);
  const columns = useMemo<ColumnDef<OuraTrait>[]>(
    () => [
      {
        accessorKey: "traitName",
        header: "Trait",
        cell: ({ row }) => (
          <button className="font-medium text-left hover:underline" onClick={() => openTrait(row.original.collection)}>
            {row.original.traitName ?? short(row.original.collection)}
          </button>
        ),
      },
      { accessorKey: "collection", header: "Collection", cell: ({ row }) => <span className="font-mono text-xs">{short(row.original.collection)}</span> },
      { accessorKey: "traitCount", header: "Traits" },
      { id: "partners", header: "Partners", cell: ({ row }) => csv(row.original.partnerNames) || "-" },
      { id: "categories", header: "Categories", cell: ({ row }) => csv(row.original.categoryNames) || "-" },
      { id: "rarities", header: "Rarities", cell: ({ row }) => csv(row.original.rarityNames) || "-" },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Button variant="ghost" size="icon" onClick={() => openTrait(row.original.collection)} aria-label="View details">
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [setLocation],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Traits</h2>
        <p className="text-sm text-zinc-400">Trait collections, mints, updates, and burns.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Traits Table</CardTitle>
          <Button onClick={() => setIsCreateTraitOpen(true)}>Add Trait</Button>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={columns}
            data={query.data?.items ?? []}
            total={query.data?.total ?? 0}
            params={params}
            onParamsChange={setParams}
            exportName="oura-traits.csv"
          />
        </CardContent>
      </Card>
      <Sheet open={isCreateTraitOpen} onOpenChange={setIsCreateTraitOpen}>
        <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
          <SheetHeader className="border-b px-6 py-5">
            <SheetTitle>Add Trait</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-73px)]">
            <div className="p-6">
              <TraitForm mode="create" onSuccess={() => setIsCreateTraitOpen(false)} />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
}
